import { api } from './api';

/**
 * Retailers API wrappers.
 *
 * Backed by `GET /api/retailers/:id` (see api/src/routes/retailers.js).
 * Returns the retailer row and a count of active listings — used by the
 * RetailerDetailPage to render the profile + a "Browse N listings" CTA.
 *
 * `trustScore` arrives as a Sequelize DECIMAL(4,2) string. UI code should
 * coerce with `Number()` when computing stars/percentages.
 */

export interface Retailer {
  id: string;
  name: string;
  slug: string;
  baseUrl: string;
  logoUrl: string | null;
  trustScore: number | string;
  scrapeIntervalMinutes: number;
  freshnessWindowMinutes: number;
  isDegraded: boolean;
  isActive: boolean;
  lastScrapedAt: string | null;
}

/**
 * Aggregate-only user-rating summary (issue #9, spec §12.3). Never carries
 * per-rater data. `established` is false until at least `minSample` distinct
 * raters exist — the UI surfaces a "not yet enough ratings" state below it.
 * This signal is SEPARATE from the curated `trustScore`.
 */
export interface RetailerRatingSummary {
  count: number;
  average: number | null;
  trimmedAverage: number | null;
  distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
  established: boolean;
  minSample: number;
}

export interface RetailerDetailResponse {
  retailer: Retailer;
  listingCount: number;
  ratingSummary: RetailerRatingSummary;
}

export const retailersApi = {
  getRetailer(id: string): Promise<RetailerDetailResponse> {
    return api.get<RetailerDetailResponse>(`/retailers/${encodeURIComponent(id)}`);
  },
};

export default retailersApi;
